import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, useTheme } from '@mui/material';
import useClinicDocument from 'hooks/useClinicDocument';
import useOptions from 'hooks/useOptions';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DialogClose from 'views/components/buttons/DialogClose';
import CenteredLoading from 'views/components/CenteredLoading';
import CheckboxGroup from 'views/components/inputs/CheckBoxGroupOther';
import FormContainer from 'views/components/inputs/FormContainer';
import MultiFileUploader from 'views/components/inputs/MultiFileUploader';
import * as Yup from 'yup';

const EditAddDocumentModal = ({ editingFolderId, onClose, documentId, docId }: any) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const [isFileSelected, setIsFileSelected] = useState(true);
  const [error, setError] = useState<string>('');

  const handleFileSelectionChange = (selected: boolean) => {
    setIsFileSelected(selected);
  };
  const validationSchema = Yup.object().shape({
    targetAudience: Yup.array()
      .of(Yup.string())
      .required(intl.formatMessage({ defaultMessage: 'Target Audience is required' }))
  });

  type FormValues = Yup.InferType<typeof validationSchema>;
  const { document, updateDocument, isLoadingDocument, isUpdatingDocument } = useClinicDocument({
    documentId: documentId ?? editingFolderId
  });
  const [fileList, setFileList] = useState<File[] | null>(null);
  const doc = document.documents.filter((document) => document._id === docId)[0];

  React.useEffect(() => {
    if (fileList && fileList.length > 0) {
      setIsFileSelected(false);
    } else {
      setIsFileSelected(true);
    }
  }, [fileList]);

  const onFormSubmit = async (values: FormValues) => {
    const formData = new FormData();
    const { targetAudience } = values;
    let data;

    if (fileList) {
      fileList.forEach((file) => {
        formData.append('file', file);
      });
    }
    if (!fileList && !docId) {
      enqueueSnackbar(intl.formatMessage({ defaultMessage: 'add at least 1 document' }), { variant: 'error' });
      return;
    }

    formData.set('targetAudience', JSON.stringify(targetAudience));

    if (docId) {
      formData.set('docId', docId);
    }

    data = await updateDocument(formData);
    enqueueSnackbar(data.message, { variant: 'success' });
    onClose();
  };

  const initialValues = {
    targetAudience: !!docId ? doc.targetAudience : []
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {!!docId ? <FormattedMessage defaultMessage="Edit Documents" /> : <FormattedMessage defaultMessage="Upload Documents" />}
      </DialogTitle>
      <DialogClose onClose={onClose} />
      <Divider />
      <DialogContent>
        {isLoadingDocument ? (
          <CenteredLoading />
        ) : (
          <FormContainer validation={validationSchema} defaultValues={initialValues} onSuccess={onFormSubmit}>
            <EditAddDocumentFields
              fileList={fileList}
              setFileList={setFileList}
              docId={docId}
              onFileSelectionChange={handleFileSelectionChange}
              error={error}
              setError={setError}
            />

            <DialogActions sx={{ mt: 3 }}>
              <Divider />
              <Button variant="contained" type="submit" sx={{ width: 180 }} disabled={isFileSelected || Boolean(error)}>
                {!!docId ? <FormattedMessage defaultMessage="Update" /> : <FormattedMessage defaultMessage="Upload" />}
              </Button>
            </DialogActions>
          </FormContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const EditAddDocumentFields = ({ fileList, setFileList, docId, handleFileSelectionChange, error, setError }: any) => {
  const theme = useTheme();
  const intl = useIntl();
  const { roles } = useOptions();

  return (
    <Stack spacing={4}>
      <Box
        sx={{ border: 1, borderColor: '#ddd', borderStyle: 'solid', borderRadius: theme.shape.borderRadius, p: 2, width: 'fit-content' }}
      >
        <CheckboxGroup
          name="targetAudience"
          label={intl.formatMessage({ defaultMessage: 'Target Audience' })}
          options={
            roles
              ?.filter((role: any) => !['NOUS_SUPPORT', 'CLIENT'].includes(role.roleName))
              ?.map((role: any) => ({
                label: intl.locale === 'fr' ? role?.roleNameFrench : role.roleName,
                id: role.roleName
              })) ?? []
          }
          row
        />
      </Box>

      {!!!docId && (
        <Box>
          <MultiFileUploader
            error={error}
            setError={setError}
            name="documents"
            maxSizeInMB={10}
            fileList={fileList}
            label={intl.formatMessage({ defaultMessage: 'Add Document' })}
            description={intl.formatMessage({ defaultMessage: 'Select all your file you want to upload (Max. {size} MB)' }, { size: 10 })}
            endIcon={<AddIcon />}
            setFileList={setFileList}
            onFileSelectionChange={handleFileSelectionChange}
            single
          />
        </Box>
      )}
    </Stack>
  );
};

export default EditAddDocumentModal;
